import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CheckCircle, Sparkles, TrendingUp, Mail, ArrowRight, Calculator, Handshake, Landmark } from 'lucide-react';
import { toast } from 'sonner';

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isRequested, setIsRequested] = useState(false);

  const handleBetaRequest = (e) => {
    e.preventDefault();
    if (email) {
      const subject = 'Inscription newsletter Bozzam';
      const body = `Bonjour,\n\nJe souhaite m'abonner à la newsletter Bozzam.\n\nEmail: ${email}\n\nMerci !`;
      window.open(`mailto:contact@bozzam.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
      
      setTimeout(() => {
        setIsRequested(true);
        toast.success('Inscription envoyée ! Bienvenue dans la communauté Bozzam.');
      }, 1000);
    }
  };

  if (isRequested) {
    return (
      <section className="py-24 bg-gradient-to-br from-green-600 via-blue-700 to-blue-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fadeIn">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-4xl font-display font-black text-white mb-4">
              Inscription confirmée ! 🎉
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Vous recevrez très bientôt nos meilleurs conseils et guides directement dans votre boîte mail.
            </p>
            <Button 
              className="btn-modern bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-xl font-semibold hover:shadow-lg"
              onClick={() => window.open(process.env.REACT_APP_BOZZAM_BLOG_URL, '_blank')}
            >
              Continuer sur le blog
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-gradient-to-br from-blue-800 via-blue-900 to-blue-950 relative overflow-hidden">
      {/* Background animé */}
      <div className="absolute inset-0 bg-black/10"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/10 rounded-full filter blur-3xl animate-float"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/10 rounded-full filter blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
      
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          {/* Badge */}
          {/* Titre principal */}
          <h2 className="text-4xl md:text-5xl font-display font-black text-white mb-6">
            Abonnez-vous à la 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">
              newsletter
            </span>
            <br />
            Bozzam
          </h2>
          
          <p className="text-lg text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed">
            Une application tout-en-un pour gérer votre freelance : <strong className="text-white">facturation</strong>, 
            <strong className="text-white"> missions</strong>, <strong className="text-white">documents comptables</strong> 
            (K-bis, bilan), <strong className="text-white">relevés bancaires</strong>,  
            <strong className="text-white"> simulations stratégiques</strong> pour tester la viabilité 
            de vos projets avant de vous lancer, et un <strong className="text-white">réseau de partenaires</strong> (banque, assurance, comptable, agence web, fournisseur matériel informatique avec remise). Tout ce dont vous avez besoin sur une seule plateforme !
          </p>
        </div>

        {/* Formulaire beta test */}
        <form onSubmit={handleBetaRequest} className="max-w-md mx-auto mb-12">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input 
                type="email" 
                placeholder="Votre email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border border-white/20 text-white placeholder:text-white/60 backdrop-blur-md h-14 text-lg rounded-xl focus:border-white/40 focus:bg-white/15"
                required
              />
            </div>
            <Button 
              type="submit"
              className="btn-modern bg-white text-blue-600 hover:bg-gray-100 px-8 h-14 rounded-xl font-semibold hover:shadow-lg group"
            >
              <Mail className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
              S'abonner à la newsletter
            </Button>
          </div>
        </form>

        {/* Fonctionnalités */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12">
          <div className="text-center text-white">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
              <CheckCircle className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-1 text-sm">Facturation</h3>
            <p className="text-blue-100 text-xs">Devis & factures</p>
          </div>
          
          <div className="text-center text-white">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
              <TrendingUp className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-1 text-sm">Missions</h3>
            <p className="text-blue-100 text-xs">Trouver & gérer</p>
          </div>
          
          <div className="text-center text-white">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
              <Sparkles className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-1 text-sm">Comptabilité</h3>
            <p className="text-blue-100 text-xs">K-bis, bilans</p>
          </div>
          
          <div className="text-center text-white">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
              <Calculator className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-1 text-sm">Simulations</h3>
            <p className="text-blue-100 text-xs">Tests viabilité</p>
          </div>

          <div className="text-center text-white">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
              <Handshake className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-1 text-sm">Partenaires</h3>
            <p className="text-blue-100 text-xs">Banque, comptable, assurance</p>
          </div>

          <div className="text-center text-white">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
              <Landmark className="h-6 w-6" />
            </div>
            <h3 className="font-semibold mb-1 text-sm">Suivi bancaire</h3>
            <p className="text-blue-100 text-xs">Synchronisation comptes</p>
          </div>
        </div>

        {/* Info beta */}
        <div className="text-center">
          <div className="inline-flex flex-col items-center gap-3 px-6 py-4 rounded-xl glass border border-white/20 backdrop-blur-lg">
            <p className="text-white font-medium text-sm">
              Version beta • Accès limité • Feedback apprécié
            </p>
            <p className="text-blue-100 text-xs max-w-md">
              Aidez-nous à améliorer l'application en testant les nouvelles fonctionnalités avant tout le monde
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;